/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import RouteParser from '../router/route-parser';
import Debugger from '../debug/debugger';
import Constants from '../constants';
import Utils from '../utils/utils';
import { localeBundlesManager } from '../locale/bundles-manager';
import type { ConfigurationType } from '../initialiser/configurations';

import type {
  CookieComplianceMethod,
  EnvironmentInfoObj,
  UpdateFeatureMapObj,
  FeaturesFromInfinity,
  Bundle
} from './types';

/**
 * Provides APIs to access state-full properties like properties from pxRequestor, OperatorID and Application pages
 */

class EnvironmentInfo {
  public environmentInfoObject: EnvironmentInfoObj | null;
  public isPortalLoaded: boolean;
  public pageName: string | null;
  public KeyMapping: any;
  public isAssignmentHeaderHidden: boolean;
  public coexistenceMeta: {
    appType: string;
    isCoexistence: boolean;
  };

  public brandingInfo: any;
  public themeOverrides: ConfigurationType['theme'];
  public locale?: string;
  private cookieComplianceMethod: CookieComplianceMethod;
  private isExtraCallRequiredForDefaultHome: string;
  private isWebEmbed?: boolean;

  constructor() {
    this.environmentInfoObject = null;
    this.isPortalLoaded = false;
    this.pageName = null;
    this.KeyMapping = {};
    this.isAssignmentHeaderHidden = false;
    this.coexistenceMeta = {
      appType: '',
      isCoexistence: false
    };
    this.brandingInfo = null;
    this.cookieComplianceMethod = 'none';
    this.isExtraCallRequiredForDefaultHome = 'false';
  }

  setEnvironmentInfo(environmentInfoObject: EnvironmentInfoObj) {
    this.environmentInfoObject = environmentInfoObject;
    const previousFeatures = this.environmentInfoObject?.features ||
      Utils.convertObjectWithStringToBoolean(this.environmentInfoObject?.pyFeatures as FeaturesFromInfinity) || {
        granularUIRefresh: false
      };
    this.environmentInfoObject.features = {
      ...previousFeatures,
      granularUIRefresh: (previousFeatures.granularUIRefresh ||
        previousFeatures.pyEnableGranularUIRefresh ||
        false) as boolean
    };
    localeBundlesManager.setLanguagePack(this.environmentInfoObject.pyLanguagePacks || []);
  }

  /**
   * Returns operator user name
   *
   * @example <caption>PCore.getEnvironmentInfo().getOperatorName()</caption>
   * const operatorName = PCore.getEnvironmentInfo().getOperatorName();
   *
   * @returns user name
   */
  getOperatorName() {
    return this.environmentInfoObject?.pxOperator?.pyUserName;
  }

  /**
   * Returns operator work group
   *
   * @example <caption>PCore.getEnvironmentInfo().getOperatorWorkGroup()</caption>
   * const operatorWorkGroup = PCore.getEnvironmentInfo().getOperatorWorkGroup();
   *
   * @returns work group name
   */
  getOperatorWorkGroup() {
    return this.environmentInfoObject?.pxOperator?.pyWorkGroup;
  }

  /**
   * Returns operator identifier
   *
   * @example <caption>PCore.getEnvironmentInfo().getOperatorIdentifier()</caption>
   * const operatorIdentifier = PCore.getEnvironmentInfo().getOperatorIdentifier();
   *
   * @returns identifier
   */
  getOperatorIdentifier() {
    return this.environmentInfoObject?.pxOperator?.pyUserIdentifier;
  }

  /**
   * Returns operator image insKey
   *
   * @example <caption>PCore.getEnvironmentInfo().getOperatorImageInsKey()</caption>
   * const operatorImgInsKey = PCore.getEnvironmentInfo().getOperatorImageInsKey();
   *
   * @returns image insKey
   */
  getOperatorImageInsKey() {
    return this.environmentInfoObject?.pxOperator?.pyImageInsKey;
  }

  /**
   * Returns application label
   *
   * @example <caption>PCore.getEnvironmentInfo().getApplicationLabel()</caption>
   * const applicationLabel = PCore.getEnvironmentInfo().getApplicationLabel();
   *
   * @returns application label
   */
  getApplicationLabel() {
    return this.environmentInfoObject?.pxApplication?.pyLabel;
  }

  getPortalName() {
    return RouteParser.getPortalName();
  }

  /**
   * Returns application name
   *
   * @example <caption>PCore.getEnvironmentInfo().getApplicationName()</caption>
   * const applicationLabel = PCore.getEnvironmentInfo().getApplicationName();
   *
   * @returns application name
   */
  getApplicationName() {
    return this.environmentInfoObject?.pxApplication?.pyRuleName;
  }

  /**
   * Returns useLocale defined
   *
   * @example <caption>PCore.getEnvironmentInfo().getUseLocale()</caption>
   * const useLocale = PCore.getEnvironmentInfo().getUseLocale();
   *
   * @returns useLocale
   */
  getUseLocale() {
    return this.environmentInfoObject?.pxLocale?.pyUseLocale;
  }

  /**
   * Return Access group
   *
   * @example <caption>PCore.getEnvironmentInfo().getAccessGroup()</caption>
   * const accessGroup = PCore.getEnvironmentInfo().getAccessGroup();
   */
  getAccessGroup() {
    return this.environmentInfoObject?.pxOperator?.pyAccessGroup;
  }

  /**
   * Returns timeZone defined
   *
   * @example <caption>PCore.getEnvironmentInfo().getTimeZone()</caption>
   * const timeZone = PCore.getEnvironmentInfo().getTimeZone();
   *
   * @returns timezone
   */
  getTimeZone() {
    return this.environmentInfoObject?.pxLocale?.pyTimeZone?.trim() || undefined;
  }

  /**
   * Returns theme overrides defined
   *
   * @example <caption>PCore.getEnvironmentInfo().getTheme()</caption>
   * const theme = PCore.getEnvironmentInfo().getTheme();
   *
   * @returns theme
   */
  getTheme() {
    return this.themeOverrides;
  }

  /**
   * updates theme overrides defined
   * @param theme - object with overrides to the theme definition
   * @example <caption>PCore.getEnvironmentInfo().setTheme(theme)</caption>
   * PCore.getEnvironmentInfo().setTheme(theme);
   */
  setTheme(theme: ConfigurationType['theme']) {
    this.themeOverrides = theme;
    return this.themeOverrides;
  }

  setIsWebEmbed(isWebEmbed: boolean) {
    this.isWebEmbed = isWebEmbed;
    return this.isWebEmbed;
  }

  isWSSPortal() {
    return this.environmentInfoObject?.pyPortalTemplate === Constants.PORTAL_TYPE.WSS;
  }

  /**
   * Returns theme branding info defined
   *
   * @example <caption>PCore.getEnvironmentInfo().getBrandingInfo()</caption>
   * const theme = PCore.getEnvironmentInfo().getBrandingInfo();
   *
   * @returns theme branding info
   */
  private getBrandingInfo() {
    return this.brandingInfo;
  }

  /**
   * updates theme branding info defined
   * @param brandingInfo - object with overrides to the theme branding info
   * @example <caption>PCore.getEnvironmentInfo().setBrandingInfo(brandingInfo)</caption>
   * PCore.getEnvironmentInfo().setBrandingInfo(brandingInfo);
   */
  setBrandingInfo(brandingInfo: object) {
    this.brandingInfo = brandingInfo;
    return this.brandingInfo;
  }

  /**
   * Returns cookie compliance/privacy compliance method value defined on currently rendered portal.
   *
   * @example getCookieComplianceMethod()
   * Below api, returns 'default' as privacy compliance method for current rendered portal
   * ```
   * const cookieComplianceMethod = PCore.getEnvironmentInfo().getCookieComplianceMethod();
   * ```
   */
  getCookieComplianceMethod() {
    return this.cookieComplianceMethod;
  }

  /**
   * Sets cookie compliance/privacy compliance method value for current portal.
   * @param cookieComplianceMethod - selected privacy compliance method
   *
   * @example setCookieComplianceMethod()
   * Below api, sets default privacy compliance method value for current portal
   * ```
   * PCore.getEnvironmentInfo().setCookieComplianceMethod('default');
   * ```
   */
  setCookieComplianceMethod(cookieComplianceMethod: CookieComplianceMethod) {
    this.cookieComplianceMethod = cookieComplianceMethod;
  }

  /**
   * Use this API to change user's locale info on portal
   * to load localized application content.
   * This API must be called when portal is being loaded.
   * This API only changes locale in the portal but does not user's defined locale.
   * @param locale - User locale info that will be set on portal Example: de_DE, en_GB
   * @example <caption>In this example API sets user's locale to de_DE when portal is loading</caption>
   * PCore.getEnvironmentInfo().setLocale('de_DE')
   */
  setLocale(locale: string) {
    const numberOfScripts = 2;
    const localeArr: string[] = locale.replaceAll('-', '_').split('_').slice(0, numberOfScripts);
    this.locale = localeArr.join('-');
    if (this.environmentInfoObject?.pxLocale) {
      this.environmentInfoObject.pxLocale.pyUseLocale = this.locale;
    }
  }

  getLocale = () => this.locale;

  /**
   * Returns array containing all global environmental keys
   *
   * @example <caption>PCore.getEnvironmentInfo().getEnvironmentKeys()</caption>
   * const environmentKeys = PCore.getEnvironmentInfo().getEnvironmentKeys();
   *
   * @returns array containing all global environmental keys
   */
  getEnvironmentKeys() {
    return Object.keys(Constants.ENV_INFO_KEYS);
  }

  /**
   *This API obtains the system configured size limit in MB for attachments.
   *If the size limit is not configured in the system, then the default value of the size limit is 5.
   *
   * @returns the size limit.
   *
   * @example In this example, the API returns the system configured size limit for attachments.
   *
   * const maxAttachmentSize = PCore.getEnvironmentInfo().getMaxAttachmentSize();
   *
   */
  getMaxAttachmentSize() {
    return this.environmentInfoObject?.pxApplication?.pyMaxAttachmentSize;
  }

  /**
   *This API obtains the datapage to get the  operators list.
   *
   * @returns the operator datapage name.
   *
   * @example In this example, the API returns the datapage name to get the  operators list.
   *
   * const defaultOperatorDP = PCore.getEnvironmentInfo().getDefaultOperatorDP();
   *
   */
  getDefaultOperatorDP() {
    return this.environmentInfoObject?.pxApplication?.pyDefaultOperatorDP;
  }
  /**
   *This API obtains the datapage to get the  document url list.
   *
   * @returns the document datapage name.
   *
   * @example In this example, the API returns the datapage name to get the  document url list.
   *
   * const documentUrlDP = PCore.getEnvironmentInfo().getDocumentUrlDP();
   * @private
   */

  getDocumentUrlDP() {
    return this.environmentInfoObject?.pxApplication?.pyDocumentUrlDP;
  }
  /**
   *This API obtains the datapage to get the  case instance list.
   *
   * @returns the case instance datapage name.
   *
   * @example In this example, the API returns the datapage name to get the case instance list.
   *
   * const caseInstanceListDP = PCore.getEnvironmentInfo().getCaseInstanceListDP();
   *
   */

  getCaseInstanceListDP() {
    return this.environmentInfoObject?.pxApplication?.pyCaseInstanceListDP;
  }

  /**
   * Returns the base locale
   *
   * @example <caption>PCore.getEnvironmentInfo().getBaseLocale()</caption>
   * const applicationLabel = PCore.getEnvironmentInfo().getBaseLocale();
   * //Returns en_EN if the base locale is set to en_EN during application creation.
   * @returns application name
   * @ignore
   */
  getBaseLocale() {
    return this.environmentInfoObject?.pxApplication?.pyBaseLocale || 'en-US';
  }

  /**
   * Description: Constellation UI can be rendered in three different modes
   * 1. FULL_PORTAL - which includes portal rendering of the constellation
   * 2. EMBED - It is a mashup scenario, where cases or views will be rendered
   * 3. HYBRID - Constellation redered in the legacy sections or harnesses
   * 4. PREVIEW - Constellation rendered during authoring time
   * This API returns the rederingMode value, which can be any value from the above four
   * @example <caption>PCore.getEnvironmentInfo().getRenderingMode()</caption>
   * const renderingMode = PCore.getEnvironmentInfo().getRenderingMode();
   * @returns renderingMode
   */
  getRenderingMode() {
    return this.environmentInfoObject?.renderingMode;
  }

  /**
   *This API obtains if the URL params needs to be updated.
   *
   * @returns true if the URL params needs to be updated, false otherwise.
   *
   * @example In this example, the API returns if the URL params needs to be updated with UI state info.
   *
   * const shouldUseURLParams = PCore.getEnvironmentInfo().shouldUseURLParams();
   * @private
   */

  shouldUseURLParams() {
    return this.environmentInfoObject?.renderingMode === 'FULL_PORTAL' && this.getFeatureMap()?.pyUseURLParams;
  }

  /**
   * Returns a csv list of built-on app names
   *
   * @example <caption>PCore.getEnvironmentInfo().getBuiltOnAppNames()</caption>
   * const builtOnAppNames = PCore.getEnvironmentInfo().getBuiltOnAppNames();
   *
   * @returns user name
   * @ignore
   */

  getBuiltOnAppNames() {
    return this.environmentInfoObject?.pxApplication?.pyBuiltOnApplications;
  }

  // TODO: Need to remove setPreviewMode() from here since it's available already in Debugger module.
  //  Once, ASX start using Debugger module, below method will get removed.
  /**
   * This API set the previewMode to true
   * @example <caption>PCore.getEnvironmentInfo().setPreviewMode()</caption>
   * @private
   */
  setPreviewMode() {
    Debugger.setPreviewMode();
  }

  /**
   * This API sets the defaultCasePage value
   * @example <caption>PCore.getEnvironmentInfo().setDefaultCasePage("pyEmbedAssignment")</caption>
   * @ignore
   */
  setDefaultCasePage(pageName: string) {
    this.pageName = pageName;
  }

  /**
   * This API gets the defaultCasePage value
   * @example <caption>PCore.getEnvironmentInfo().getDefaultCasePage()</caption>
   * @returns pageName value which has been already set
   * @ignore
   */
  getDefaultCasePage() {
    return this.pageName;
  }

  /**
   * This API sets the DisableAssignmentHeader value
   * @example <caption>PCore.getEnvironmentInfo().disableAssignmentHeader(true)</caption>
   * @ignore
   */
  disableAssignmentHeader(value: boolean) {
    this.isAssignmentHeaderHidden = value;
  }

  /**
   * This API gets the DisableAssignmentHeader value
   * @example <caption>PCore.getEnvironmentInfo().isAssignmentHeaderDisabled()</caption>
   * @returns disableAssignmentHeader value which has been already set
   */
  private isAssignmentHeaderDisabled() {
    return this.isAssignmentHeaderHidden;
  }

  /**
   * This API gets the name of default portal assigned to an operator
   * @example <caption>if Operator has WebPortal as their default portal, PCore.getEnvironmentInfo().getDefaultPortal() will return "WebPortal"</caption>
   * @returns default portal name set to the operator
   */
  getDefaultPortal() {
    return this.environmentInfoObject?.pxOperator?.pyDefaultPortal;
  }

  /**
   * This API is used to update additional feature info to environmentInfo Object
   * @example <caption>, PCore.getEnvironmentInfo().updateFeatureMap() will return feature updated object</caption>
   * @returns feature value
   */
  private updateFeatureMap(data: UpdateFeatureMapObj) {
    const existingFeatureMap = this.environmentInfoObject?.features;
    if (this.environmentInfoObject && data) {
      if (this.environmentInfoObject?.features) {
        this.environmentInfoObject.features = { ...existingFeatureMap, ...data };
      } else {
        this.environmentInfoObject.features = { ...data };
      }
    }

    return this.getFeatureMap();
  }

  /**
   * This API exposes configured overrides for features
   * @example <caption>, PCore.getEnvironmentInfo().getFeatureMap() will return feature override object</caption>
   * @returns default is empty object
   * @ignore
   */
  getFeatureMap() {
    return this.environmentInfoObject?.features || {};
  }

  /**
   * This API is to store keyMapping for launchpad
   * @example <caption>PCore.getEnvironmentInfo().setKeyMapping({"pxCreateDateTime" : "CreateDateTime"})</caption>
   * @private
   */
  setKeyMapping(keyMap: any) {
    this.KeyMapping = keyMap;
    return this.KeyMapping;
  }

  /**
   * This API gets the corresponding launchpad identifier for the specified constellation identifier.
   * If corresponding launchpad identifier is missing it will return null
   * @param keyValue - Specific constellation identifier for which we obtain corresponding launchpad identifier
   * @example <caption>PCore.getEnvironmentInfo().getKeyMapping("pzInsKey") (i.e. mapping of Constellation identifier with launchpad identifier ex. pzInskey to ID)</caption>
   * @returns Launchpad identifier if available else null
   */
  getKeyMapping(keyValue: string): string | null {
    const keyVal = this.KeyMapping[keyValue];
    return keyVal !== undefined ? keyVal : null;
  }

  setCoexistenceMeta(data: any) {
    if (data) {
      this.coexistenceMeta = data;
    }
    return this.getCoexistenceMeta();
  }

  getCoexistenceMeta() {
    return this.coexistenceMeta;
  }

  isCoexistenceAppOfType(type: string) {
    return !!(this.coexistenceMeta.isCoexistence && this.coexistenceMeta.appType === type);
  }

  /**
   * Returns list of cases added in application cases and data
   * @example <caption>PCore.getEnvironmentInfo().getCaseTypeList()</caption>
   * const caselist = PCore.getEnvironmentInfo().getCaseTypeList();
   *
   * @returns theme
   * @ignore
   */
  getCaseTypeList() {
    return this.environmentInfoObject ? this.environmentInfoObject?.pyCaseTypeList || [] : [];
  }

  /**
   * Returns userSettings
   *
   * @example <caption>PCore.getEnvironmentInfo().getUserSettings</caption>
   * const userSettings = PCore.getEnvironmentInfo().getUserSettings();
   * @returns userSettings
   * @ignore
   */
  getUserSettings(): string | null {
    return this.environmentInfoObject?.pyUserSettings || null;
  }

  /**
   * Returns localization version
   *
   * @example <caption>PCore.getEnvironmentInfo().getLocalizationVersion()</caption>
   * @returns number
   * @ignore
   */
  getLocalizationVersion(): number {
    const pyLocalizationVersion = this.getFeatureMap().pyLocalizationVersion;
    const localizationVersion = this.getFeatureMap().localizationVersion;
    if (localizationVersion !== undefined) {
      return localizationVersion;
    }
    if (pyLocalizationVersion !== undefined) {
      return parseInt(pyLocalizationVersion, 10);
    }
    return 1;
  }

  /**
   * This function returns sorted localization bundles based on pyOrder for a given language code.
   * If the specified language is unavailable, it falls back to the default bundle which may contain overrides.
   *
   * @example <caption>PCore.getEnvironmentInfo().getLocalizationBundles()</caption>
   * @returns Bundle[]
   *
   * @example <caption>PCore.getEnvironmentInfo().getLocalizationBundles('de-DE')</caption>
   * @returns Bundle[]
   * @ignore
   */
  getLocalizationBundles(langCode = this.locale): Bundle[] {
    return localeBundlesManager.getLocalizationBundles(langCode);
  }

  /**
   * Returns Available Locale Packs based on environment and version
   *
   * For Launchpad availableLanguagePacks is populated in the featuremap so we return it
   * For Infinity we return undefined as we are dependent on the getLocalizationBundles
   *
   * @example <caption>PCore.getEnvironmentInfo().getAvailableLocalePacks()</caption>
   * @returns string[]
   * @ignore
   */
  getAvailableLocalePacks(): string[] | undefined {
    return this.getFeatureMap()?.availableLanguagePacks || undefined;
  }

  /**
   * Returns flag to make extra call for loading default home
   *
   * @example <caption>PCore.getEnvironmentInfo().getIsExtraCallRequiredForDefaultHome()</caption>
   * const isExtraCallRequiredForDefaultHome = PCore.getEnvironmentInfo().getIsExtraCallRequiredForDefaultHome();
   *
   * @returns {string} flag to make extra call for loading default home
   * @private
   */
  getIsExtraCallRequiredForDefaultHome(): string {
    return this.isExtraCallRequiredForDefaultHome;
  }

  /**
   * Sets if extra call is required to load default home data.
   * @param {boolean} isExtraCallRequiredForDefaultHome - flag to make extra call for loading default home
   *
   * @example <caption>PCore.getEnvironmentInfo().setIsExtraCallRequiredForDefaultHome(true)</caption>
   * PCore.getEnvironmentInfo().setIsExtraCallRequiredForDefaultHome("true");
   *
   * @returns {undefined}
   * @private
   */
  setIsExtraCallRequiredForDefaultHome(isExtraCallRequiredForDefaultHome: string) {
    this.isExtraCallRequiredForDefaultHome = isExtraCallRequiredForDefaultHome;
  }

  /**
   * Returns Authentication timeout value configured on access group
   *
   * @example <caption>PCore.getEnvironmentInfo().getInactivityTimeout()</caption>
   * const authenticationTimeout = PCore.getEnvironmentInfo().getInactivityTimeout();
   *
   * @returns {Number|undefined} Authentication timeout value
   * @private
   */
  getInactivityTimeout() {
    const inactivityTimeout: number | undefined = this.environmentInfoObject?.pxOperator?.pyAuthenticationTimeout;
    return inactivityTimeout ? Number(inactivityTimeout) : inactivityTimeout;
  }

  /**
   * This API exposes isUnifiedObject flag present in environment-info of launchpad
   * @example <caption> PCore.getEnvironmentInfo().isUnifiedObject() will return isUnifiedObject flag </caption>
   * @returns default false
   * @private
   */
  isUnifiedObject() {
    return this.environmentInfoObject?.features?.isUnifiedObject || false;
  }

  getRuntime(): string {
    if (typeof window !== 'undefined' && window.document) {
      return 'Browser';
    }
    return 'Native';
  }
}

export default new EnvironmentInfo();
